import { useRouter } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getCookie, setCookie } from "@tanstack/react-start/server";
import { createContext, useContext } from "react";
import { Route } from "@/routes/__root";

type Theme = "dark" | "light";

const themeCookieName = "tss-ui-theme";

type ThemeProviderProps = {
	children: React.ReactNode;
};

type ThemeProviderState = {
	setTheme: (theme: Theme | "system") => void;
};

const initialState: ThemeProviderState = {
	setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

/**
 * Server function to get the current theme from cookies.
 * Returns 'dark', 'light', or '' if not set.
 */
export const getThemeServerFn = createServerFn().handler(async () => {
	const cookieTheme = getCookie(themeCookieName);

	if (cookieTheme === "dark" || cookieTheme === "light") {
		return cookieTheme as Theme;
	}

	return "";
});

export const setThemeServerFn = createServerFn({ method: "POST" })
	.inputValidator((v: unknown) => {
		if (v === "light" || v === "dark" || v === "system") {
			return v as Theme | "system";
		}

		throw new Error("Invalid theme");
	})
	.handler(async ({ data }) => {
		if (data === "system") {
			return setCookie(themeCookieName, "");
		}

		return setCookie(themeCookieName, data);
	});

export function ThemeProvider({ children, ...props }: ThemeProviderProps) {
	const { theme } = Route.useLoaderData();

	const router = useRouter();

	function setTheme(newTheme: Theme | "system") {
		setThemeServerFn({
			data: newTheme,
		}).then(() => router.invalidate());
	}

	const value = {
		theme,
		setTheme,
	};

	return (
		<ThemeProviderContext.Provider {...props} value={value}>
			{children}
		</ThemeProviderContext.Provider>
	);
}

export const useTheme = () => {
	const context = useContext(ThemeProviderContext);

	if (context === undefined) throw new Error("useTheme must be used within a ThemeProvider");

	return context;
};
